import memoize from "lodash/memoize";
import { isValidPhoneNumber } from "./isValidPhoneNumber";
import { DateTime } from "luxon";
import { classNameOf } from "../backoffice.utils";

export const phoneNumber = memoize(
  (message: string = "Bitte gib eine gültige Telefonnummer ein.") =>
    (value: string): undefined | string => {
      if (value) {
        if (!isValidPhoneNumber(value)) {
          return message;
        }
        const normalized = value.replaceAll(/[^\d+]/g, "");
        if (!(normalized.startsWith("+") || normalized.startsWith("0"))) {
          return "Bitte gib die Telefonnummer mit Vorwahl ein.";
        }
      }
    },
);

export const url = memoize(
  (message: string = "Bitte gib eine gültige URL ein.") =>
    (value: string): undefined | string => {
      if (value) {
        try {
          new URL(value);
        } catch {
          return message;
        }
      }
    },
);

export const email = memoize(
  (message: string = "Bitte gib eine gültige E-Mail-Adresse ein.") =>
    (value: string): undefined | string => {
      if (value) {
        // simple email regex according to https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
        const emailRegex =
          /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!emailRegex.test(value)) {
          return message;
        }
      }
    },
);

export const date = memoize(
  (message: string = "Bitte gib ein gültiges Datum ein.") =>
    (value: any): undefined | string => {
      if (!value) {
        return;
      }
      if (typeof value === "string") {
        if (!DateTime.fromISO(value).isValid) {
          return message;
        }
      } else if (DateTime.isDateTime(value)) {
        if (!value.isValid) {
          return message;
        }
      }
    },
);

export const dateMinYear = memoize(
  (minYear: number, message: string = "Ungültiges Jahr.") =>
    (value: any): undefined | string => {
      if (!value) {
        return;
      }
      if (typeof value === "string") {
        const dateTime = DateTime.fromISO(value);
        if (dateTime.isValid && dateTime.get("year") < minYear) {
          return message;
        }
      } else if (DateTime.isDateTime(value)) {
        if (value.isValid && value.get("year") < minYear) {
          return message;
        }
      } else {
        throw new Error(`Unexpected value for dateMinYear validator of type ${classNameOf(value)}: ${value}`);
      }
    },
);

export const dateMaxYear = memoize(
  (maxYear: number, message: string = "Ungültiges Jahr.") =>
    (value: any): undefined | string => {
      if (!value) {
        return;
      }
      if (typeof value === "string") {
        const dateTime = DateTime.fromISO(value);
        if (dateTime.isValid && dateTime.get("year") > maxYear) {
          return message;
        }
      } else if (DateTime.isDateTime(value)) {
        if (value.isValid && value.get("year") > maxYear) {
          return message;
        }
      } else {
        throw new Error(`Unexpected value for dateMaxYear validator of type ${classNameOf(value)}: ${value}`);
      }
    },
);

export const dateNotInTheFuture = memoize(
  (message: string = "Ungültiges Datum. (Das Datum darf nicht in der Zukunft liegen.)") =>
    (value: any): undefined | string => {
      if (!value) {
        return;
      }
      const endOfToday = DateTime.now().setZone("Europe/Berlin").endOf("day");
      if (typeof value === "string") {
        const dateTime = DateTime.fromISO(value);
        if (dateTime.isValid && dateTime > endOfToday) {
          return message;
        }
      } else if (DateTime.isDateTime(value)) {
        if (value.isValid && value > endOfToday) {
          return message;
        }
      } else {
        throw new Error(`Unexpected value for dateNotInTheFuture validator of type ${classNameOf(value)}: ${value}`);
      }
    },
);

export const dateNotInThePast = memoize(
  (message: string = "Ungültiges Datum. (Das Datum darf nicht in der Vergangenheit liegen.)") =>
    (value: any): undefined | string => {
      if (!value) {
        return;
      }
      const startOfToday = DateTime.now().setZone("Europe/Berlin").startOf("day");
      if (typeof value === "string") {
        const dateTime = DateTime.fromISO(value);
        if (dateTime.isValid && dateTime < startOfToday) {
          return message;
        }
      } else if (DateTime.isDateTime(value)) {
        if (value.isValid && value < startOfToday) {
          return message;
        }
      } else {
        throw new Error(`Unexpected value for dateInTheFuture validator of type ${classNameOf(value)}: ${value}`);
      }
    },
);
